// OWNER: Agent C. Payment history + "Record EMI payment" — prefilled with the
// EMI amount and today's date, so paying is two clicks.
import { useState } from "react";
import { useParams } from "react-router";
import { Text } from "@cloudflare/kumo/components/text";
import { Empty } from "@cloudflare/kumo/components/empty";
import { Button } from "@cloudflare/kumo/components/button";
import { Table } from "@cloudflare/kumo/components/table";
import { Meter } from "@cloudflare/kumo/components/meter";
import { Dialog } from "@cloudflare/kumo/components/dialog";
import { Input } from "@cloudflare/kumo/components/input";
import { useKumoToastManager } from "@cloudflare/kumo/components/toast";
import { formatMoney, formatPercent } from "@shared/format";
import { useLoan, useLoanPayments, recordLoanPayment, useIsAdmin } from "../../lib/hooks";
import { PageHeader } from "../../components/PageHeader";
import { CardSkeleton, TableSkeleton } from "../../components/Skeletons";

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

export default function LoanDetailPage() {
  const { loanId } = useParams();
  const id = Number(loanId);
  const loan = useLoan(id);
  const payments = useLoanPayments(id);
  const toasts = useKumoToastManager();
  const isAdmin = useIsAdmin();

  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [paidOn, setPaidOn] = useState(today());
  const [submitting, setSubmitting] = useState(false);

  if (loan.status === "loading") return <CardSkeleton />;
  if (loan.status === "error") return <Text variant="error">{loan.error.message}</Text>;

  const l = loan.data;
  const paidPct = l.principal > 0 ? ((l.principal - l.outstandingPrincipal) / l.principal) * 100 : 0;

  function openDialog() {
    setAmount(l.emiAmount ? String(l.emiAmount) : "");
    setPaidOn(today());
    setOpen(true);
  }

  async function submitPayment() {
    setSubmitting(true);
    try {
      await recordLoanPayment(id, { paidOn, amount: Number(amount) });
      toasts.add({ title: "Payment recorded", variant: "success" });
      setOpen(false);
      loan.reload();
      payments.reload();
    } catch (e) {
      toasts.add({ title: "Couldn't record payment", content: e instanceof Error ? e.message : undefined, variant: "error" });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="max-w-2xl space-y-6 pb-8">
      <PageHeader
        title={l.name}
        description={l.lender ?? undefined}
        action={
          <Button variant="primary" disabled={!isAdmin} title={isAdmin ? undefined : "Admins only"} onClick={openDialog}>
            Record EMI payment
          </Button>
        }
      />

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <Stat label="Outstanding" value={formatMoney(l.outstandingPrincipal, l.currency)} />
        <Stat label="EMI" value={l.emiAmount ? formatMoney(l.emiAmount, l.currency) : "—"} />
        <Stat label="Next due" value={l.nextDueDate ?? "—"} />
        <Stat label="Rate" value={l.interestRate != null ? `${l.interestRate}%` : "—"} />
      </div>

      <Meter label="Paid off" value={paidPct} customValue={formatPercent(paidPct, 1).replace("+", "")} />

      <div>
        <Text variant="heading" as="h2">
          Payment history
        </Text>
        <div className="mt-3">
          {payments.status === "loading" && <TableSkeleton rows={3} />}
          {payments.status === "error" && <Text variant="error">{payments.error.message}</Text>}
          {payments.status === "success" && payments.data.length === 0 && <Empty size="sm" title="No payments recorded yet" />}
          {payments.status === "success" && payments.data.length > 0 && (
            <Table>
              <Table.Header>
                <Table.Row>
                  <Table.Head>Date</Table.Head>
                  <Table.Head>Amount</Table.Head>
                  <Table.Head>Principal</Table.Head>
                  <Table.Head>Interest</Table.Head>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {payments.data.map((p) => (
                  <Table.Row key={p.id}>
                    <Table.Cell>{p.paidOn}</Table.Cell>
                    <Table.Cell>{formatMoney(p.amount, l.currency)}</Table.Cell>
                    <Table.Cell>{p.principalComponent != null ? formatMoney(p.principalComponent, l.currency) : "—"}</Table.Cell>
                    <Table.Cell>{p.interestComponent != null ? formatMoney(p.interestComponent, l.currency) : "—"}</Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          )}
        </div>
      </div>

      <Dialog.Root open={open} onOpenChange={setOpen}>
        <Dialog className="p-6">
          <Dialog.Title className="text-lg font-semibold">Record EMI payment</Dialog.Title>
          <div className="mt-4 space-y-4">
            <Input label="Amount" type="number" min={0} step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} />
            <Input label="Paid on" type="date" value={paidOn} onChange={(e) => setPaidOn(e.target.value)} />
          </div>
          <div className="mt-6 flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" loading={submitting} onClick={submitPayment}>
              Save payment
            </Button>
          </div>
        </Dialog>
      </Dialog.Root>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-kumo-hairline p-3">
      <Text variant="secondary" size="xs">
        {label}
      </Text>
      <div className="mt-1 text-base font-medium text-kumo-strong">{value}</div>
    </div>
  );
}
